import { Injectable } from '@angular/core';
import {ServerService} from "./ServerService";
import {HttpClient} from "@angular/common/http";
import { WhatsAppTelefone, WhatsAppIntegracao } from '../cloud-whatsapp/config-whatsapp/whatsapp.interface';

export interface CloudWhatsappSdkConfig {
  appId: string;
  configId: string;
  version: string;
  redirectUri: string;
}

@Injectable({
  providedIn: 'root'
})
export class CloudWhatsappService extends ServerService {
  constructor(protected http: HttpClient) {
    super(http);
  }

  obtenhaConfiguracaoSdk(): Promise<CloudWhatsappSdkConfig> {
    console.log('[CloudWhatsapp] Obtendo configuração do SDK do Facebook');

    return this.obtenha('/cloudwhatsapp/sdk-config', {})
      .then((response: CloudWhatsappSdkConfig) => {
        console.log('[CloudWhatsapp] Configuração do SDK recebida:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao obter configuração do SDK:', error);
        throw error;
      });
  }

  exchangeCodeForToken(authorizationCode: string, redirectUri?: string): Promise<string> {
    const dados = {
      code: authorizationCode,
      redirectUri
    };

    return this.facaPost('/whatsapp/exchange-token', dados)
      .then(response => {
        // Log detalhado para debug
        console.log('[CloudWhatsapp] Estrutura da resposta de token:',
          JSON.stringify({
            temSuccess: !!response?.success,
            temSucesso: !!response?.sucesso,
            temObjeto: !!response?.objeto,
            temData: !!response?.data,
            formato: typeof response,
            chaves: response ? Object.keys(response) : [],
            resposta: response
          })
        );

        // Verificar estrutura da resposta (pode ser diferente dependendo do formato da API)
        let accessToken = null;

        if (response && response.success && response.access_token) {
          // Formato direto da resposta
          accessToken = response.access_token;
        } else if (response && response.objeto && response.objeto.access_token) {
          // Formato com objeto wrapper
          accessToken = response.objeto.access_token;
        } else if (response && response.sucesso && response.data && response.data.access_token) {
          // Formato com wrapper do Resposta
          accessToken = response.data.access_token;
        } else if (response && response.sucesso && response.objeto && response.objeto.access_token) {
          // Formato combinado sucesso+objeto
          accessToken = response.objeto.access_token;
        } else if (response && typeof response === 'object' && response.access_token) {
          // Resposta direta (sem wrappers)
          accessToken = response.access_token;
        }

        if (!accessToken) {
          console.error('[CloudWhatsapp] Falha ao extrair access_token da resposta:', response);
          // Tentativa de extrair o token diretamente como string (último recurso)
          if (typeof response === 'string' && response.length > 20) {
            console.log('[CloudWhatsapp] Tentando interpretar resposta como token string direto');
            return response;
          }
          throw new Error('Falha ao obter access token: formato de resposta inválido');
        }

        return accessToken;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao trocar código por token:', error);
        throw error;
      });
  }

  obtenhaTelefones(accessToken: string): Promise<WhatsAppTelefone[]> {
    console.log('[CloudWhatsapp] Obtendo telefones com token:', accessToken ? accessToken.substring(0, 10) + '...' : 'vazio');

    if (!accessToken) {
      console.error('[CloudWhatsapp] Token de acesso ausente na chamada obtenhaTelefones');
      return Promise.reject(new Error('Token de acesso é obrigatório'));
    }

    const dados = {
      at: accessToken
    };

    console.log('[CloudWhatsapp] Enviando requisição para obter telefones');

    return this.obtenha('/whatsapp/telefones', dados)
      .then(response => {
        console.log('[CloudWhatsapp] Resposta obtenhaTelefones:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao obter telefones:', error);
        throw error;
      });
  }

  associe(accessToken: string, waba: WhatsAppTelefone): Promise<any> {
    console.log('[CloudWhatsapp] Iniciando associação de WhatsApp');
    console.log('[CloudWhatsapp] Token presente:', !!accessToken);
    console.log('[CloudWhatsapp] Dados do WABA:', JSON.stringify(waba));

    if (!accessToken) {
      console.error('[CloudWhatsapp] Token de acesso ausente na chamada associe');
      return Promise.reject(new Error('Token de acesso é obrigatório'));
    }

    if (!waba || !waba.waba_id) {
      console.error('[CloudWhatsapp] Dados do WhatsApp Business inválidos');
      return Promise.reject(new Error('Dados do WhatsApp Business são obrigatórios'));
    }

    const dados = {
      at: accessToken,
      waba: waba
    };

    console.log('[CloudWhatsapp] Enviando requisição para associar número', JSON.stringify(dados));

    return this.facaPost('/whatsapp/associe', dados)
      .then(response => {
        console.log('[CloudWhatsapp] Resposta da associação:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao associar WhatsApp:', error);
        throw error;
      });
  }

  desassocie(): Promise<any> {
    console.log('[CloudWhatsapp] Iniciando desassociação de WhatsApp');

    return this.facaPost('/whatsapp/desassocie', {})
      .then(response => {
        console.log('[CloudWhatsapp] Resposta da desassociação:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao desassociar WhatsApp:', error);
        throw error;
      });
  }

  carregueDados(): Promise<WhatsAppIntegracao> {
    console.log('[CloudWhatsapp] Carregando dados da integração');

    return this.obtenha('/whatsapp/integracao', {})
      .then(response => {
        console.log('[CloudWhatsapp] Dados de integração carregados:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao carregar dados da integração:', error);
        throw error;
      });
  }

  obtenhaResumoConversas(periodo: string = '30dias'): Promise<any> {
    console.log('[CloudWhatsapp] Obtendo resumo de conversas para período:', periodo);

    return this.obtenha('/whatsapp/resumo-conversas', { periodo })
      .then(response => {
        console.log('[CloudWhatsapp] Resumo de conversas recebido');
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao obter resumo de conversas:', error);
        throw error;
      });
  }
}
